import { ref, onUnmounted, type ComputedRef, type Ref } from 'vue';

/** Time to move from a nav trigger onto the mega panel (gap / diagonal paths). */
const MEGA_MENU_CLOSE_DELAY_MS = 600;

/**
 * Desktop mega menu: hover/focus open state with delayed close on mouse leave.
 */
export function useMegaMenu(
  useDesktopMegaMenu: ComputedRef<boolean>,
  isSearchOpen: Ref<boolean>
) {
  const activeDesktopSectionKey = ref<string | null>(null);
  /** True while the delayed-close timer runs (pointer left trigger, not yet on panel). */
  const megaMenuClosingSoon = ref(false);
  let megaCloseTimer: number | null = null;

  function clearMegaCloseTimer(): void {
    if (megaCloseTimer !== null) {
      clearTimeout(megaCloseTimer);
      megaCloseTimer = null;
    }
    megaMenuClosingSoon.value = false;
  }

  function scheduleMegaClose(): void {
    clearMegaCloseTimer();
    megaMenuClosingSoon.value = true;
    megaCloseTimer = window.setTimeout(() => {
      activeDesktopSectionKey.value = null;
      megaMenuClosingSoon.value = false;
      megaCloseTimer = null;
    }, MEGA_MENU_CLOSE_DELAY_MS);
  }

  function closeDesktopDropdown(): void {
    clearMegaCloseTimer();
    activeDesktopSectionKey.value = null;
  }

  function onDesktopNavItemEnter(sectionKey: string): void {
    if (!useDesktopMegaMenu.value || isSearchOpen.value) return;
    clearMegaCloseTimer();
    activeDesktopSectionKey.value = sectionKey;
  }

  function onDesktopNavItemLeave(): void {
    if (!useDesktopMegaMenu.value) return;
    scheduleMegaClose();
  }

  function onMegaPanelEnter(): void {
    if (!useDesktopMegaMenu.value) return;
    clearMegaCloseTimer();
  }

  function onMegaPanelLeave(): void {
    if (!useDesktopMegaMenu.value) return;
    clearMegaCloseTimer();
    activeDesktopSectionKey.value = null;
  }

  function onMegaPanelFocusIn(): void {
    clearMegaCloseTimer();
  }

  function onDesktopTriggerFocus(sectionKey: string): void {
    if (!useDesktopMegaMenu.value || isSearchOpen.value) return;
    clearMegaCloseTimer();
    activeDesktopSectionKey.value = sectionKey;
  }

  onUnmounted(() => {
    clearMegaCloseTimer();
  });

  return {
    activeDesktopSectionKey,
    megaMenuClosingSoon,
    clearMegaCloseTimer,
    closeDesktopDropdown,
    onDesktopNavItemEnter,
    onDesktopNavItemLeave,
    onMegaPanelEnter,
    onMegaPanelLeave,
    onMegaPanelFocusIn,
    onDesktopTriggerFocus,
  };
}
